import React from 'react';
import {
  Archive,
  Check,
  Copy,
  Download,
  EyeOff,
  FileText,
  Info,
  Loader2,
  Mail,
  MoreVertical,
  Pencil,
  Pin,
  Plus,
  RefreshCw,
  Share2,
  Tag as TagIcon,
  Trash2,
  Users,
} from 'lucide-react';
import { format } from 'date-fns';
import type { Agent, Conversation, Message } from '../../types';
import { useChatStore } from '../../stores/chatStore';
import { getAuthHeaders, useAuthStore } from '../../stores/authStore';
import { cn, formatShortcut, ui } from '../../utils';
import AgentAvatar from '../Common/AgentAvatar';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { LoadingFallback } from '../Common/LoadingFallback';
import { useConfirm } from '../Common/ConfirmDialog';

const TagManagerModal = React.lazy(() => import('../Layout/TagManagerModal').then((m) => ({ default: m.TagManagerModal })));
const ConvertToGroupModal = React.lazy(() => import('../Layout/ConvertToGroupModal').then((m) => ({ default: m.ConvertToGroupModal })));

interface ChatHeaderProps {
  agent: Agent | null;
  conversation: Conversation | null;
  messages?: Message[];
  onToggleInfoPanel: () => void;
  onNewSession: () => void;
  onRenameConversation?: (title: string) => Promise<void> | void;
  className?: string;
}

type ExportFormat = 'markdown' | 'text' | 'pdf';

function slugifyFilename(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'chat';
}

function formatExportTimestamp(timestamp?: string) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, 'yyyy-MM-dd HH:mm');
}

function getMessageSender(agent: Agent | null, conversation: Conversation | null, message: Message) {
  if (message.role === 'user') {
    return message.user?.name || 'User';
  }
  if (message.role === 'assistant') {
    return message.agentId || conversation?.agentName || agent?.name || 'Assistant';
  }
  if (message.role === 'system') {
    return 'System';
  }
  return 'Tool';
}

function buildConversationExport(agent: Agent | null, conversation: Conversation | null, messages: Message[] = [], formatType: Exclude<ExportFormat, 'pdf'>) {
  if (!conversation) {
    return '';
  }

  const title = conversation.title || conversation.groupName || agent?.name || 'Chat export';
  const lines = messages.flatMap((message) => {
    const timestamp = formatExportTimestamp(message.createdAt || message.timestamp) || 'Unknown time';
    const sender = getMessageSender(agent, conversation, message);
    const content = String(message.content || '').trim() || '(no text)';
    const attachmentLines = (message.attachments || []).map((attachment) => (
      formatType === 'markdown'
        ? `- Attachment: [${attachment.filename || 'Attachment'}](${attachment.url})`
        : `- Attachment: ${attachment.filename || 'Attachment'}${attachment.url ? ` (${attachment.url})` : ''}`
    ));

    if (formatType === 'markdown') {
      return [
        `## ${timestamp} · ${sender}`,
        '',
        content,
        ...(attachmentLines.length > 0 ? ['', ...attachmentLines] : []),
        '',
      ];
    }

    return [
      `[${timestamp}] ${sender}`,
      content,
      ...(attachmentLines.length > 0 ? attachmentLines : []),
      '',
    ];
  });

  if (formatType === 'markdown') {
    return [
      `# ${title}`,
      '',
      `Exported: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`,
      '',
      ...lines,
    ].join('\n');
  }

  return [
    title,
    `Exported: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`,
    '',
    ...lines,
  ].join('\n');
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildConversationPrintHtml(agent: Agent | null, conversation: Conversation | null, messages: Message[] = []) {
  if (!conversation) {
    return '';
  }

  const title = conversation.title || conversation.groupName || agent?.name || 'Chat export';
  const exportedAt = format(new Date(), 'yyyy-MM-dd HH:mm');
  const messageHtml = messages.map((message) => {
    const timestamp = formatExportTimestamp(message.createdAt || message.timestamp) || 'Unknown time';
    const sender = getMessageSender(agent, conversation, message);
    const content = escapeHtml(String(message.content || '').trim() || '(no text)').replace(/\n/g, '<br />');
    const attachments = (message.attachments || []).map((attachment) => {
      const label = escapeHtml(attachment.filename || 'Attachment');
      const url = escapeHtml(attachment.url || '');
      return `<li><a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a></li>`;
    }).join('');

    return `
      <section class="message">
        <div class="meta">${escapeHtml(timestamp)} · ${escapeHtml(sender)}</div>
        <div class="content">${content}</div>
        ${attachments ? `<ul class="attachments">${attachments}</ul>` : ''}
      </section>
    `;
  }).join('');

  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title)}</title>
        <style>
          :root { color-scheme: light; }
          body { font-family: Inter, -apple-system, BlinkMacSystemFont, sans-serif; margin: 0; color: #0f172a; background: #ffffff; }
          .page { max-width: 860px; margin: 0 auto; padding: 32px 28px 48px; }
          h1 { margin: 0 0 8px; font-size: 28px; }
          .subtitle { color: #475569; font-size: 14px; margin-bottom: 24px; }
          .message { border: 1px solid #e2e8f0; border-radius: 16px; padding: 16px; margin-bottom: 16px; page-break-inside: avoid; }
          .meta { color: #475569; font-size: 12px; font-weight: 600; margin-bottom: 10px; }
          .content { font-size: 14px; line-height: 1.7; white-space: normal; word-break: break-word; }
          .attachments { margin: 12px 0 0; padding-left: 18px; color: #1d4ed8; }
          .attachments a { color: #1d4ed8; text-decoration: none; }
          .attachments a:hover { text-decoration: underline; }
          @media print { .page { padding: 0; } }
        </style>
      </head>
      <body>
        <main class="page">
          <h1>${escapeHtml(title)}</h1>
          <div class="subtitle">Exported ${escapeHtml(exportedAt)}</div>
          ${messageHtml || '<p>No messages yet.</p>'}
        </main>
      </body>
    </html>
  `;
}

function printConversationToPdf(agent: Agent | null, conversation: Conversation | null, messages: Message[] = []) {
  if (!conversation) return;

  const printWindow = window.open('', '_blank', 'width=960,height=720');
  if (!printWindow) {
    return;
  }

  printWindow.document.open();
  printWindow.document.write(buildConversationPrintHtml(agent, conversation, messages));
  printWindow.document.close();
  printWindow.focus();
  printWindow.onload = () => {
    printWindow.print();
  };
}

function downloadConversationExport(agent: Agent | null, conversation: Conversation | null, messages: Message[] = [], formatType: ExportFormat) {
  if (!conversation) return;

  if (formatType === 'pdf') {
    printConversationToPdf(agent, conversation, messages);
    return;
  }

  const content = buildConversationExport(agent, conversation, messages, formatType);
  const extension = formatType === 'markdown' ? 'md' : 'txt';
  const mimeType = formatType === 'markdown' ? 'text/markdown' : 'text/plain';
  const filename = `${slugifyFilename(conversation.title || conversation.groupName || agent?.name || 'chat')}.${extension}`;
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// D8-09 gave share links a TTL (default 7 days); surface it so people know
// how long the link lasts, and offer "Generate new link" to rotate the token
// (invalidating the old link) without leaving the modal.
function formatShareExpiry(expiresAt: string | null) {
  if (!expiresAt) {
    return 'This link does not expire.';
  }

  const parsed = new Date(expiresAt);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  if (parsed.getTime() <= Date.now()) {
    return 'This link has expired — generate a new one.';
  }

  return `Link expires ${format(parsed, 'MMM d, yyyy h:mm a')}.`;
}

function ShareConversationModal({
  isOpen,
  isLoading,
  shareUrl,
  expiresAt,
  error,
  onClose,
  onRegenerate,
}: {
  isOpen: boolean;
  isLoading: boolean;
  shareUrl: string;
  expiresAt: string | null;
  error: string | null;
  onClose: () => void;
  onRegenerate: () => void;
}) {
  // D7-06 (FE-6.5): shared copy hook replaces the local copied/timeout state.
  const { copied, copy, reset } = useCopyToClipboard();
  const dialogRef = useFocusTrap<HTMLDivElement>(isOpen, onClose);

  React.useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  if (!isOpen) {
    return null;
  }

  const handleCopy = async () => {
    if (!shareUrl) {
      return;
    }

    await copy(shareUrl);
  };

  return (
    <div className={ui.modalBackdrop} onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-conversation-title"
        aria-describedby="share-conversation-description"
        tabIndex={-1}
        className={cn(ui.modalPanel, 'max-w-lg')}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <h2 id="share-conversation-title" className="text-base font-semibold text-foreground">Share conversation</h2>
            <p id="share-conversation-description" className="mt-1 text-sm text-muted-foreground">Anyone with this link can open a read-only copy of the chat.</p>
          </div>
          <button type="button" onClick={onClose} className={cn(ui.button.icon, 'h-10 w-10')} aria-label="Close share dialog">
            ×
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          {isLoading ? (
            <div className="flex items-center gap-3 rounded-2xl border border-border bg-background/70 px-4 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Generating share link...
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-4 text-sm text-red-200">{error}</div>
          ) : (
            <>
              <div className="rounded-2xl border border-border bg-background/70 p-3">
                <div className="break-all text-sm text-foreground">{shareUrl}</div>
              </div>
              {formatShareExpiry(expiresAt) ? (
                <p className="text-xs text-muted-foreground">{formatShareExpiry(expiresAt)}</p>
              ) : null}
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={onRegenerate}
                  className={cn(ui.button.base, ui.button.secondary)}
                  aria-label="Generate a new share link (the current link stops working)"
                  title="The current link stops working"
                >
                  <RefreshCw className="h-4 w-4" />
                  Generate new link
                </button>
                <button
                  type="button"
                  onClick={() => void handleCopy()}
                  className={cn(ui.button.base, ui.button.primary, 'px-4 py-2.5')}
                  aria-label={copied ? 'Share link copied' : 'Copy share link'}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy link'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryModal({
  isOpen,
  isLoading,
  summary,
  error,
  onClose,
}: {
  isOpen: boolean;
  isLoading: boolean;
  summary: string;
  error: string | null;
  onClose: () => void;
}) {
  const dialogRef = useFocusTrap<HTMLDivElement>(isOpen, onClose);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={ui.modalBackdrop} onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="conversation-summary-title"
        tabIndex={-1}
        className={cn(ui.modalPanel, 'max-w-2xl')}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <h2 id="conversation-summary-title" className="text-base font-semibold text-foreground">Conversation summary</h2>
            <p className="mt-1 text-sm text-muted-foreground">A quick recap of the conversation so far.</p>
          </div>
          <button type="button" onClick={onClose} className={cn(ui.button.icon, 'h-10 w-10')} aria-label="Close summary dialog">
            ×
          </button>
        </div>

        <div className="px-5 py-5">
          {isLoading ? (
            <div className="flex items-center gap-3 rounded-2xl border border-border bg-background/70 px-4 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Building summary...
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-4 text-sm text-red-200">{error}</div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto rounded-2xl border border-border bg-background/70 px-4 py-4 text-sm leading-7 text-foreground whitespace-pre-wrap">
              {summary || 'No summary available yet.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ChatHeader({
  agent,
  conversation,
  messages = [],
  onToggleInfoPanel,
  onNewSession,
  onRenameConversation,
  className,
}: ChatHeaderProps) {
  const toggleConversationPinned = useChatStore((state) => state.toggleConversationPinned);
  const toggleConversationArchived = useChatStore((state) => state.toggleConversationArchived);
  const hideConversation = useChatStore((state) => state.hideConversation);
  const markConversationUnread = useChatStore((state) => state.markConversationUnread);
  const deleteConversation = useChatStore((state) => state.deleteConversation);
  const convertConversationToGroup = useChatStore((state) => state.convertConversationToGroup);
  const authUser = useAuthStore((state) => state.impersonatedUser ?? state.user);
  const confirm = useConfirm();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isConvertModalOpen, setIsConvertModalOpen] = React.useState(false);
  const [isEditingTitle, setIsEditingTitle] = React.useState(false);
  const [isSavingTitle, setIsSavingTitle] = React.useState(false);
  const [draftTitle, setDraftTitle] = React.useState('');
  const [isTagManagerOpen, setIsTagManagerOpen] = React.useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = React.useState(false);
  const [isShareLoading, setIsShareLoading] = React.useState(false);
  const [shareUrl, setShareUrl] = React.useState('');
  const [shareExpiresAt, setShareExpiresAt] = React.useState<string | null>(null);
  const [shareError, setShareError] = React.useState<string | null>(null);
  // D7-06 (FE-6.5): shared copy hook replaces the local copied/timeout state.
  const { copied: copiedShareLink, copy: copyShareLink, reset: resetCopiedShareLink } = useCopyToClipboard();
  const [isSummaryModalOpen, setIsSummaryModalOpen] = React.useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = React.useState(false);
  const [summaryText, setSummaryText] = React.useState('');
  const [summaryError, setSummaryError] = React.useState<string | null>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const titleInputRef = React.useRef<HTMLInputElement>(null);
  const isGroup = Boolean(conversation?.isGroup);
  const title = conversation?.title || conversation?.groupName || agent?.name || (isGroup ? 'Group Chat' : 'New Chat');
  const canRenameConversation = Boolean(conversation && onRenameConversation);
  // Convert is owner-only (server enforces this too); local-only conversations
  // without a persisted owner belong to the current user by construction.
  const canConvertToGroup = Boolean(
    conversation
    && !isGroup
    && (!conversation.userId || !authUser?.id || conversation.userId === authUser.id),
  );
  const newConversationShortcut = formatShortcut(['Mod', 'N']);

  React.useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (menuRef.current && target && !menuRef.current.contains(target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  React.useEffect(() => {
    setDraftTitle(title);
    setIsEditingTitle(false);
    setIsSavingTitle(false);
    setIsMenuOpen(false);
    setIsShareModalOpen(false);
    setIsShareLoading(false);
    setShareUrl('');
    setShareExpiresAt(null);
    setShareError(null);
    resetCopiedShareLink();
    setSummaryError(null);
    setSummaryText('');
    setIsSummaryLoading(false);
    setIsSummaryModalOpen(false);
    setIsConvertModalOpen(false);
  }, [conversation?.id, conversation?.title, conversation?.groupName, resetCopiedShareLink, title]);

  React.useEffect(() => {
    if (isEditingTitle) {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }
  }, [isEditingTitle]);

  const startRename = React.useCallback(() => {
    if (!canRenameConversation) {
      return;
    }

    setDraftTitle(title);
    setIsEditingTitle(true);
    setIsMenuOpen(false);
  }, [canRenameConversation, title]);

  const cancelRename = React.useCallback(() => {
    setDraftTitle(title);
    setIsEditingTitle(false);
    setIsSavingTitle(false);
  }, [title]);

  const commitRename = React.useCallback(async () => {
    if (!conversation || !onRenameConversation) {
      setIsEditingTitle(false);
      return;
    }

    const nextTitle = draftTitle.trim();
    if (!nextTitle) {
      cancelRename();
      return;
    }

    if (nextTitle === title) {
      setIsEditingTitle(false);
      return;
    }

    setIsSavingTitle(true);
    try {
      await onRenameConversation(nextTitle);
    } catch (error) {
      console.error('Failed to rename conversation:', error);
    } finally {
      setIsSavingTitle(false);
      setIsEditingTitle(false);
    }
  }, [cancelRename, conversation, draftTitle, onRenameConversation, title]);

  const getShareUrl = React.useCallback(async (options: { regenerate?: boolean } = {}) => {
    if (!conversation) {
      throw new Error('Conversation is unavailable');
    }

    if (shareUrl && !options.regenerate) {
      return shareUrl;
    }

    const response = await fetch(`/api/conversations/${conversation.id}/share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders({ includeImpersonation: true }),
      },
      // D8-09: regenerate rotates the token server-side, invalidating the
      // previous link and refreshing the expiry window.
      ...(options.regenerate ? { body: JSON.stringify({ regenerate: true }) } : {}),
    });
    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(payload?.error || 'Failed to create share link');
    }

    const token = payload?.data?.shareToken || payload?.data?.token;
    const serverShareUrl = payload?.data?.shareUrl;
    const nextShareUrl = serverShareUrl || (token ? `${window.location.origin}/shared/${token}` : '');
    if (!nextShareUrl) {
      throw new Error('Share link is unavailable');
    }

    setShareUrl(nextShareUrl);
    setShareExpiresAt(typeof payload?.data?.shareTokenExpiresAt === 'string' ? payload.data.shareTokenExpiresAt : null);
    return nextShareUrl;
  }, [conversation, shareUrl]);

  const handleShare = React.useCallback(async () => {
    if (!conversation) {
      return;
    }

    setIsMenuOpen(false);
    setIsShareModalOpen(true);
    setIsShareLoading(true);
    setShareError(null);

    try {
      await getShareUrl();
    } catch (error) {
      setShareUrl('');
      setShareExpiresAt(null);
      setShareError(error instanceof Error ? error.message : 'Failed to create share link');
    } finally {
      setIsShareLoading(false);
    }
  }, [conversation, getShareUrl]);

  const handleRegenerateShareLink = React.useCallback(async () => {
    if (!conversation) {
      return;
    }

    setIsShareLoading(true);
    setShareError(null);

    try {
      await getShareUrl({ regenerate: true });
    } catch (error) {
      setShareUrl('');
      setShareExpiresAt(null);
      setShareError(error instanceof Error ? error.message : 'Failed to create share link');
    } finally {
      setIsShareLoading(false);
    }
  }, [conversation, getShareUrl]);

  const handleCopyLink = React.useCallback(async () => {
    if (!conversation) {
      return;
    }

    try {
      const nextShareUrl = await getShareUrl();
      const didCopy = await copyShareLink(nextShareUrl);
      if (!didCopy) {
        // Clipboard access denied — fall back to the dialog so the user can
        // copy the link manually.
        console.error('Failed to copy conversation link — opening share dialog instead.');
        setIsShareModalOpen(true);
      }
    } catch (error) {
      console.error('Failed to create conversation link:', error);
      resetCopiedShareLink();
    }
  }, [conversation, copyShareLink, getShareUrl, resetCopiedShareLink]);

  const handleSummarize = React.useCallback(async () => {
    if (!conversation) {
      return;
    }

    setIsMenuOpen(false);
    setIsSummaryModalOpen(true);
    setIsSummaryLoading(true);
    setSummaryError(null);

    try {
      const response = await fetch(`/api/conversations/${conversation.id}/summarize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders({ includeImpersonation: true }),
        },
        body: JSON.stringify({ prompt: 'Please provide a brief summary of our conversation so far' }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(payload?.error || payload?.message || 'Failed to summarize conversation');
      }

      const summary = payload?.data?.summary || payload?.summary || payload?.message || '';
      setSummaryText(String(summary || '').trim());
    } catch (error) {
      setSummaryText('');
      setSummaryError(error instanceof Error ? error.message : 'Failed to summarize conversation');
    } finally {
      setIsSummaryLoading(false);
    }
  }, [conversation]);

  const handleDeleteConversation = React.useCallback(async () => {
    if (!conversation) {
      return;
    }

    const confirmed = await confirm({
      title: 'Delete conversation?',
      message: `"${conversation.title || title}" and its messages will be permanently deleted.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!confirmed) {
      return;
    }

    setIsMenuOpen(false);

    try {
      await deleteConversation(conversation.id);
      // D5-10 (FE-2.2): the focused trigger just disappeared with the
      // conversation — move focus to the sidebar so keyboard users aren't
      // dropped at document.body.
      document.getElementById('conversation-sidebar')?.focus();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  }, [confirm, conversation, deleteConversation, title]);

  const handleExport = React.useCallback((formatType: ExportFormat) => {
    if (!conversation) {
      return;
    }

    downloadConversationExport(agent, conversation, messages, formatType);
    setIsMenuOpen(false);
  }, [agent, conversation, messages]);

  return (
    <>
      <div className={cn('relative z-20 shrink-0 flex items-center justify-between border-b border-border bg-card/95 px-4 py-3 backdrop-blur sm:px-5', className)}>
        <div className="flex min-w-0 items-center gap-3">
          {isGroup ? (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl border border-border bg-background text-blue-300">
              <Users className="h-4 w-4" />
            </div>
          ) : agent ? (
            <AgentAvatar agent={{ name: agent.name, avatar: agent.avatar }} size="md" />
          ) : null}

          <div className="flex min-w-0 items-center gap-2">
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                value={draftTitle}
                onChange={(event) => setDraftTitle(event.target.value)}
                onBlur={() => void commitRename()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void commitRename();
                  }
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    cancelRename();
                  }
                }}
                disabled={isSavingTitle}
                className={cn(ui.input, 'max-w-sm px-2.5 py-1 text-sm font-semibold')}
                aria-label="Conversation title"
              />
            ) : (
              <h1 className="truncate text-[15px] font-semibold text-foreground">{title}</h1>
            )}

            {!isGroup ? (
              <button
                type="button"
                onClick={onNewSession}
                className={cn(ui.button.icon, 'h-8 w-8')}
                title={`New conversation (${newConversationShortcut})`}
                aria-label="New Conversation"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onToggleInfoPanel}
            className={cn(ui.button.icon, 'h-9 w-9')}
            title="Info"
            aria-label="Open info panel"
          >
            <Info className="h-4 w-4" />
          </button>

          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setIsMenuOpen((open) => !open)}
              className={cn(ui.button.icon, 'h-9 w-9')}
              title="More actions"
              aria-label="More conversation actions"
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
            >
              <MoreVertical className="h-4 w-4" />
            </button>

            {isMenuOpen ? (
              <div className="absolute right-0 top-11 z-50 w-60 rounded-xl border border-border bg-card py-1 shadow-2xl shadow-black/30" role="menu" aria-label="Conversation actions">
                {!isGroup ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      onNewSession();
                    }}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label="Start a new chat"
                  >
                    <Plus className="h-4 w-4" />
                    New chat
                  </button>
                ) : null}
                {canRenameConversation ? (
                  <button
                    type="button"
                    onClick={startRename}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label="Rename conversation"
                  >
                    <Pencil className="h-4 w-4" />
                    Rename conversation
                  </button>
                ) : null}

                {canConvertToGroup ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsConvertModalOpen(true);
                    }}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label="Convert to group chat"
                  >
                    <Users className="h-4 w-4" />
                    Convert to group chat
                  </button>
                ) : null}

                {conversation && (!isGroup || canRenameConversation) ? <div className="mx-3 my-1 border-t border-border" /> : null}

                {conversation ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      void toggleConversationPinned(conversation.id);
                    }}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label={conversation.isPinned ? 'Unpin conversation' : 'Pin conversation'}
                  >
                    <Pin className="h-4 w-4" />
                    {conversation.isPinned ? 'Unpin conversation' : 'Pin conversation'}
                  </button>
                ) : null}
                {conversation ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      void toggleConversationArchived(conversation.id);
                    }}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label={conversation.archivedAt ? 'Unarchive conversation' : 'Archive conversation'}
                  >
                    <Archive className="h-4 w-4" />
                    {conversation.archivedAt ? 'Unarchive conversation' : 'Archive conversation'}
                  </button>
                ) : null}
                {/* AC-HIDE-2: hide only offered on group conversations */}
                {conversation && isGroup ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      void hideConversation(conversation.id);
                    }}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label="Hide from list"
                  >
                    <EyeOff className="h-4 w-4" />
                    Hide from list
                  </button>
                ) : null}
                {/* AC-MU-4: mark-unread only when not already unread */}
                {conversation && !conversation.isUnread && (conversation.unreadCount ?? 0) === 0 ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      void markConversationUnread(conversation.id);
                    }}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label="Mark as unread"
                  >
                    <Mail className="h-4 w-4" />
                    Mark as unread
                  </button>
                ) : null}
                {conversation ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsTagManagerOpen(true);
                    }}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label="Manage tags"
                  >
                    <TagIcon className="h-4 w-4" />
                    Manage tags
                  </button>
                ) : null}
                {conversation ? (
                  <button
                    type="button"
                    onClick={() => void handleShare()}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label="Share conversation"
                  >
                    <Share2 className="h-4 w-4" />
                    Share
                  </button>
                ) : null}
                {conversation ? (
                  <button
                    type="button"
                    onClick={() => handleExport('markdown')}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label="Export as Markdown"
                  >
                    <Download className="h-4 w-4" />
                    Export as Markdown
                  </button>
                ) : null}
                {conversation ? (
                  <button
                    type="button"
                    onClick={() => handleExport('text')}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label="Export as Text"
                  >
                    <Download className="h-4 w-4" />
                    Export as Text
                  </button>
                ) : null}
                {conversation ? (
                  <button
                    type="button"
                    onClick={() => handleExport('pdf')}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label="Print conversation to PDF"
                  >
                    <Download className="h-4 w-4" />
                    Print to PDF
                  </button>
                ) : null}
                {conversation ? (
                  <button
                    type="button"
                    onClick={() => void handleCopyLink()}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label={copiedShareLink ? 'Conversation link copied' : 'Copy conversation link'}
                  >
                    <Copy className="h-4 w-4" />
                    {copiedShareLink ? 'Copied link' : 'Copy link'}
                  </button>
                ) : null}
                {conversation ? (
                  <button
                    type="button"
                    onClick={() => void handleSummarize()}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors duration-150 hover:bg-background"
                    role="menuitem"
                    aria-label="Summarize conversation"
                  >
                    <FileText className="h-4 w-4" />
                    Summarize
                  </button>
                ) : null}
                {conversation ? <div className="mx-3 my-1 border-t border-border" /> : null}
                {conversation ? (
                  <button
                    type="button"
                    onClick={() => void handleDeleteConversation()}
                    className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-300 transition-colors duration-150 hover:bg-red-500/10"
                    role="menuitem"
                    aria-label="Delete conversation"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete conversation
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {isTagManagerOpen ? (
        <React.Suspense fallback={<LoadingFallback />}>
          <TagManagerModal
            isOpen={isTagManagerOpen}
            onClose={() => setIsTagManagerOpen(false)}
            conversation={conversation}
          />
        </React.Suspense>
      ) : null}

      {isConvertModalOpen && conversation ? (
        <React.Suspense fallback={<LoadingFallback />}>
          <ConvertToGroupModal
            isOpen={isConvertModalOpen}
            conversation={conversation}
            onClose={() => setIsConvertModalOpen(false)}
            onConvert={(name, memberEmails) => convertConversationToGroup(conversation.id, name, memberEmails)}
          />
        </React.Suspense>
      ) : null}

      <ShareConversationModal
        isOpen={isShareModalOpen}
        isLoading={isShareLoading}
        shareUrl={shareUrl}
        expiresAt={shareExpiresAt}
        error={shareError}
        onClose={() => setIsShareModalOpen(false)}
        onRegenerate={() => void handleRegenerateShareLink()}
      />

      <SummaryModal
        isOpen={isSummaryModalOpen}
        isLoading={isSummaryLoading}
        summary={summaryText}
        error={summaryError}
        onClose={() => setIsSummaryModalOpen(false)}
      />
    </>
  );
}

export default ChatHeader;
